import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  MapPin,
  Users,
  Clock,
  ChevronLeft,
  Share2,
  Heart,
  Check,
  Info,
} from 'lucide-react';
import {
  PropertyGallery,
  Button,
  ShimmerButton,
  SlideToConfirm,
  NumberInput,
  Card,
  Badge,
  ResponsiveDialog,
  StickyActionBar,
  useToast,
  useCountdown,
  useViewport,
  cn,
  formatNaira,
  formatNumber,
  calculateProgress,
} from '@raffle/ui';
import { useAuth } from '../providers/AuthProvider';
import { usePropertiesStore } from '../stores/propertiesStore';
import { useTicketsStore } from '../stores/ticketsStore';

export const PropertyDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isDesktop, isMobile } = useViewport();
  const { isAuthenticated } = useAuth();
  const toast = useToast();

  const { currentProperty, isLoading, error, fetchProperty } = usePropertiesStore();
  const { purchaseTickets, isPurchasing, clearError: clearTicketError } = useTicketsStore();

  const [ticketQuantity, setTicketQuantity] = useState(1);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);

  useEffect(() => {
    if (id) void fetchProperty(id);
  }, [fetchProperty, id]);

  const property = currentProperty;
  const countdown = useCountdown(property?.drawDate || new Date());

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (isLoading) {
    return (
      <div className="page-container flex items-center justify-center min-h-[50vh]">
        <div className="text-center text-text-secondary">Loading property…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container flex items-center justify-center min-h-[50vh]">
        <div className="text-center">
          <h2 className="font-display text-2xl font-bold text-text-primary mb-4">
            Unable to Load Property
          </h2>
          <p className="text-text-secondary mb-6">{error}</p>
          <Button onClick={() => navigate('/properties')}>Browse Properties</Button>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="page-container flex items-center justify-center min-h-[50vh]">
        <div className="text-center">
          <h2 className="font-display text-2xl font-bold text-text-primary mb-4">
            Property Not Found
          </h2>
          <p className="text-text-secondary mb-6">
            The property you're looking for doesn't exist or has been removed.
          </p>
          <Button onClick={() => navigate('/properties')}>
            Browse Properties
          </Button>
        </div>
      </div>
    );
  }

  const progress = calculateProgress(property.ticketsSold, property.totalTickets);
  const remainingTickets = property.totalTickets - property.ticketsSold;
  const maxTickets = Math.min(remainingTickets, 100);
  const totalCost = property.ticketPrice * ticketQuantity;

  const handlePurchase = async () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/properties/${id}` } });
      return;
    }
    setShowPurchaseModal(true);
  };

  const handleConfirmPurchase = async () => {
    if (!id) return;
    clearTicketError();
    const result = await purchaseTickets(id, ticketQuantity, false);
    if (!result) {
      toast.error('Purchase Failed', 'Unable to purchase tickets');
      return;
    }

    setShowPurchaseModal(false);
    toast.success('Tickets Purchased!', `You've purchased ${ticketQuantity} ticket(s) for ${property.title}`);
    setTicketQuantity(1);
    void fetchProperty(id);
  };

  const handleShare = async () => {
    if (navigator.share) {
      await navigator.share({
        title: property.title,
        text: `Check out this property raffle: ${property.title}`,
        url: window.location.href,
      });
    } else {
      await navigator.clipboard.writeText(window.location.href);
      toast.info('Link Copied', 'Property link copied to clipboard');
    }
  };

  return (
    <div className="page-container">
      {/* Back Button (Mobile) */}
      {isMobile && (
        <div className="fixed top-4 left-4 z-50">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full bg-surface-0/80 backdrop-blur-sm border border-white/10"
          >
            <ChevronLeft className="w-5 h-5 text-text-primary" />
          </button>
        </div>
      )}

      {/* Gallery */}
      <section className={cn(isMobile ? '' : 'content-wrapper pt-8')}>
        <PropertyGallery
          images={(property.images || []).map((img, i) => ({ src: img.url, alt: img.alt || `${property.title} - Image ${i + 1}` }))}
          className={isMobile ? '' : 'rounded-vault-xl overflow-hidden'}
        />
      </section>

      {/* Content */}
      <section className="content-wrapper py-8">
        <div className={cn(isDesktop ? 'grid grid-cols-3 gap-12' : 'space-y-8')}>
          {/* Main Content */}
          <div className={cn(isDesktop ? 'col-span-2' : '', 'space-y-8')}>
            {/* Header */}
            <div>
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <Badge variant={property.status === 'active' ? 'success' : 'default'} className="mb-3">
                    {property.status.charAt(0).toUpperCase() + property.status.slice(1)}
                  </Badge>
                  <h1 className="font-display text-2xl md:text-3xl lg:text-4xl font-bold text-text-primary">
                    {property.title}
                  </h1>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button
                    onClick={() => setIsFavorited(!isFavorited)}
                    className={cn(
                      'p-3 rounded-full border transition-colors',
                      isFavorited
                        ? 'bg-vault-error/20 border-vault-error/30 text-vault-error'
                        : 'bg-surface-2 border-white/10 text-text-secondary hover:text-text-primary'
                    )}
                  >
                    <Heart className={cn('w-5 h-5', isFavorited && 'fill-current')} />
                  </button>
                  <button
                    onClick={handleShare}
                    className="p-3 rounded-full bg-surface-2 border border-white/10 text-text-secondary hover:text-text-primary transition-colors"
                  >
                    <Share2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <p className="flex items-center gap-2 text-text-secondary">
                <MapPin className="w-5 h-5 text-gold-500" />
                {property.location}
              </p>
            </div>

            {/* Description */}
            <div>
              <h2 className="font-display text-xl font-semibold text-text-primary mb-4">
                About This Property
              </h2>
              <p className="text-text-secondary leading-relaxed">
                {property.description}
              </p>
            </div>

            {/* Features */}
            <div>
              <h2 className="font-display text-xl font-semibold text-text-primary mb-4">
                Property Features
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {property.features.map((feature, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-3 rounded-vault bg-surface-2/50 border border-white/5"
                  >
                    <Check className="w-4 h-4 text-gold-500 flex-shrink-0" />
                    <span className="text-sm text-text-primary">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* How It Works */}
            <div className="p-6 rounded-vault-lg bg-surface-2/30 border border-white/5">
              <h3 className="flex items-center gap-2 font-display text-lg font-semibold text-text-primary mb-4">
                <Info className="w-5 h-5 text-gold-500" />
                How It Works
              </h3>
              <ol className="space-y-3">
                {[
                  'Purchase tickets for this property raffle',
                  'Each ticket gives you one entry into the draw',
                  'On the draw date, a winner is randomly selected',
                  'The winner receives full ownership of the property',
                ].map((step, index) => (
                  <li key={index} className="flex gap-3 text-sm text-text-secondary">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gold-500/20 text-gold-500 flex items-center justify-center text-xs font-bold">
                      {index + 1}
                    </span>
                    {step}
                  </li>
                ))}
              </ol>
            </div>
          </div>

          {/* Sidebar (Desktop) */}
          {isDesktop && (
            <div className="space-y-6">
              {/* Price Card */}
              <Card className="sticky top-28">
                <div className="p-6 space-y-6">
                  {/* Market Value */}
                  <div>
                    <p className="text-sm text-text-muted mb-1">Market Value</p>
                    <p className="font-mono text-2xl font-bold text-text-primary tabular-nums">
                      {formatNaira(property.marketValue)}
                    </p>
                  </div>

                  {/* Ticket Price */}
                  <div className="p-4 rounded-vault bg-gold-500/10 border border-gold-500/20">
                    <p className="text-sm text-gold-500 mb-1">Ticket Price</p>
                    <p className="font-mono text-3xl font-bold text-gold-500 tabular-nums">
                      {formatNaira(property.ticketPrice)}
                    </p>
                  </div>

                  {/* Countdown */}
                  {property.status === 'active' && !countdown.isExpired && (
                    <div>
                      <p className="text-sm text-text-muted mb-3 flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        Draw Countdown
                      </p>
                      <div className="grid grid-cols-4 gap-2">
                        {[
                          { value: countdown.days, label: 'Days' },
                          { value: countdown.hours, label: 'Hrs' },
                          { value: countdown.minutes, label: 'Min' },
                          { value: countdown.seconds, label: 'Sec' },
                        ].map((item) => (
                          <div key={item.label} className="text-center p-2 rounded-lg bg-surface-2">
                            <p className="font-mono text-xl font-bold text-text-primary tabular-nums">
                              {item.value.toString().padStart(2, '0')}
                            </p>
                            <p className="text-xs text-text-muted">{item.label}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Progress */}
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-text-muted">Tickets Sold</span>
                      <span className="font-mono text-text-primary tabular-nums">
                        {formatNumber(property.ticketsSold)} / {formatNumber(property.totalTickets)}
                      </span>
                    </div>
                    <div className="progress-bar h-3">
                      <motion.div
                        className="progress-fill"
                        initial={{ width: 0 }}
                        animate={{ width: `${progress}%` }}
                        transition={{ duration: 1, ease: 'easeOut' }}
                      />
                    </div>
                    <p className="text-xs text-text-muted mt-2">
                      {formatNumber(remainingTickets)} tickets remaining
                    </p>
                  </div>

                  {/* Participants */}
                  <div className="flex items-center gap-2 text-sm text-text-secondary">
                    <Users className="w-4 h-4 text-gold-500" />
                    <span>{formatNumber(property.ticketsSold)} participants</span>
                  </div>

                  {/* Quantity Selector */}
                  {property.status === 'active' && (
                    <>
                      <NumberInput
                        label="Number of Tickets"
                        value={ticketQuantity}
                        onChange={setTicketQuantity}
                        min={1}
                        max={maxTickets}
                      />

                      {/* Total */}
                      <div className="flex justify-between items-center p-4 rounded-vault bg-surface-2/50">
                        <span className="text-text-secondary">Total Cost</span>
                        <span className="font-mono text-xl font-bold text-text-primary tabular-nums">
                          {formatNaira(totalCost)}
                        </span>
                      </div>

                      {/* Purchase Button */}
                      <ShimmerButton
                        fullWidth
                        size="lg"
                        onClick={handlePurchase}
                        disabled={remainingTickets === 0}
                      >
                        {remainingTickets === 0 ? 'Sold Out' : 'Buy Tickets'}
                      </ShimmerButton>
                    </>
                  )}
                </div>
              </Card>
            </div>
          )}
        </div>
      </section>

      {/* Mobile Sticky Action Bar */}
      {isMobile && property.status === 'active' && (
        <StickyActionBar className="mb-0">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <p className="text-xs text-text-muted">Per Ticket</p>
              <p className="font-mono text-lg font-bold text-gold-500 tabular-nums">
                {formatNaira(property.ticketPrice)}
              </p>
            </div>
            <ShimmerButton onClick={handlePurchase} disabled={remainingTickets === 0}>
              {remainingTickets === 0 ? 'Sold Out' : 'Buy Tickets'}
            </ShimmerButton>
          </div>
        </StickyActionBar>
      )}

      {/* Purchase Modal */}
      <ResponsiveDialog
        isOpen={showPurchaseModal}
        onClose={() => !isPurchasing && setShowPurchaseModal(false)}
        title="Confirm Purchase"
        modalSize="sm"
      >
        <div className="p-6 space-y-6">
          {/* Property Summary */}
          <div className="flex gap-4 p-4 rounded-vault bg-surface-2/50">
            <img
              src={property.images?.[0]?.url || ''}
              alt=""
              className="w-20 h-14 rounded-lg object-cover"
            />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-text-primary truncate">{property.title}</p>
              <p className="text-sm text-text-muted">{property.location}</p>
            </div>
          </div>

          {/* Quantity */}
          <NumberInput
            label="Number of Tickets"
            value={ticketQuantity}
            onChange={setTicketQuantity}
            min={1}
            max={maxTickets}
          />

          {/* Summary */}
          <div className="space-y-3 p-4 rounded-vault bg-surface-2/30">
            <div className="flex justify-between text-sm">
              <span className="text-text-muted">Ticket Price</span>
              <span className="text-text-primary tabular-nums">{formatNaira(property.ticketPrice)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-text-muted">Quantity</span>
              <span className="text-text-primary tabular-nums">×{ticketQuantity}</span>
            </div>
            <div className="flex justify-between pt-3 border-t border-white/5">
              <span className="font-medium text-text-primary">Total</span>
              <span className="font-mono text-xl font-bold text-gold-500 tabular-nums">
                {formatNaira(totalCost)}
              </span>
            </div>
          </div>

          {/* Slide to Confirm (Mobile) / Button (Desktop) */}
          {isMobile ? (
            <SlideToConfirm
              onConfirm={handleConfirmPurchase}
              label="Slide to Pay"
              confirmLabel="Processing..."
              loading={isPurchasing}
            />
          ) : (
            <ShimmerButton
              fullWidth
              size="lg"
              onClick={handleConfirmPurchase}
              loading={isPurchasing}
            >
              Confirm & Pay {formatNaira(totalCost)}
            </ShimmerButton>
          )}

          <p className="text-xs text-text-muted text-center">
            By purchasing, you agree to our Terms of Service and Raffle Rules
          </p>
        </div>
      </ResponsiveDialog>
    </div>
  );
};
